/** The parts of a package row that identify it. */
export interface ParcelRow {
  id: string | undefined;
  name: string;
}

/**
 * Row identity for React keys and dnd-kit. Package names are not unique, and
 * duplicate sortable ids make dnd-kit reuse one row's rect for its namesakes.
 */
export const parcelRowId = (parcel: ParcelRow): string =>
  parcel.id ?? parcel.name;

/**
 * Adopts the ids the server persisted, then drops submitted rows that landed on
 * a package already in the list. Rows outside this save are never dropped.
 */
export function adoptPersistedParcelIds<T extends ParcelRow>(
  rows: T[],
  persistedIdBySubmittedId: ReadonlyMap<string, string | undefined>,
): T[] {
  const kept: T[] = [];
  const keptIds = new Set<string>();
  const keptNames = new Set<string>();

  for (const row of rows) {
    const submittedId = row.id;
    const submitted =
      submittedId !== undefined && persistedIdBySubmittedId.has(submittedId);
    const persistedId =
      submittedId === undefined
        ? undefined
        : persistedIdBySubmittedId.get(submittedId);
    const adopted = persistedId ? { ...row, id: persistedId } : row;

    // Same id is the same document; a submitted row that never got one was
    // absorbed by the namesake already kept above it.
    if (keptIds.has(parcelRowId(adopted))) {
      continue;
    }
    if (submitted && !persistedId && keptNames.has(adopted.name)) {
      continue;
    }

    kept.push(adopted);
    keptIds.add(parcelRowId(adopted));
    keptNames.add(adopted.name);
  }

  return kept;
}
